import * as React from "react";

import * as BigCard from "@components/big-card/index";
import { BigCardProps } from "@components/big-card/types";
import { Component as Grid } from "@components/grid/index";
import { Skeleton } from "@components/skeleton/Skeleton";
import { DaperStorySite } from "@data/daper-client";
import { DaperStory } from "@data/daper-client/types";
import {
  PersonalizationRelatedParams,
  PersonalizationStory,
} from "@data/personalization-client/types";
import { IntercomTracking } from "@interface/IntercomSlot";
import { IntercomComponentProps, IntercomInterface } from "@interface/types";
import { truthyFilter } from "@util/truthyFilter";
import { type AdRenderer } from "types/types";

import styles from "./styles.module.css";

export type Props = {
  intercom: IntercomInterface;
  personalizationRelatedParams: PersonalizationRelatedParams;
  numberOfRows?: number;
  numberOfColumns?: number;
  adRenderers?: AdRenderer[];
  title?: string;
} & IntercomComponentProps;

export const getRelatedStoriesWithImagesForComponent = async (props: {
  intercom: IntercomInterface;
  personalizationRelatedParams: PersonalizationRelatedParams;
}) => {
  const { intercom, personalizationRelatedParams } = props;

  const relatedStories = (await intercom.getRelated(
    personalizationRelatedParams
  )) as PersonalizationStory[];

  const responses = await Promise.allSettled(
    relatedStories.map(async (relatedStory) => {
      const storySearchResponse = (await intercom.getStories({
        id: relatedStory.assetID,
        "attachments.image": true,
      })) as DaperStory[];

      if (!storySearchResponse.length) {
        throw Error(
          `No stories returned from getStories with id=${relatedStory.assetID}`
        );
      }

      const story = storySearchResponse[0];

      if (!relatedStory.thumbnailUrl) {
        const ledeImageId = story.extraMedia?.lede?.default.id;

        if (!ledeImageId) return null;

        const images = await intercom.getImages({ id: ledeImageId });

        return { ...story, images };
      }

      return { ...story, images: { baseUrl: relatedStory.thumbnailUrl } };
    })
  );

  return responses
    .map((response) =>
      response && response.status === "fulfilled" ? response.value : null
    )
    .filter(truthyFilter);
};

export function RelatedStoriesBigCardGrid(props: Props) {
  const {
    intercom,
    personalizationRelatedParams,
    numberOfColumns = 5,
    numberOfRows = 1,
    adRenderers,
    title,
  } = props;

  const [componentPropsList, setComponentPropsList] = React.useState<
    BigCardProps[]
  >([]);
  const [isLoading, setIsLoading] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(true);
    getRelatedStoriesWithImagesForComponent({
      intercom,
      personalizationRelatedParams: {
        ...personalizationRelatedParams,
        limit: numberOfColumns * numberOfRows,
      },
    }).then((stories) => {
      const propsList = stories.map((story) => ({
        image: {
          assetUrl: story.images.baseUrl,
          isLazy: true,
          aspectRatio: 3 / 2,
          linkToUrl: story.url,
        },
        text: story.headlines.web,
        url: story.url,
        eyebrow: story.sites.includes(DaperStorySite.bview)
          ? { text: "Opinion" }
          : null,
      }));

      setComponentPropsList(propsList);
      setIsLoading(false);
    });
  }, [numberOfColumns, numberOfRows, personalizationRelatedParams]);

  const BigCardWithSkeleton = (componentProps: BigCardProps) => (
    <Skeleton isLoading={isLoading}>
      <div className={styles.relatedStoriesBigCardContainer}>
        <BigCard.WithBlueEyebrow {...componentProps} />
      </div>
    </Skeleton>
  );

  return (
    <IntercomTracking componentName="RelatedStoriesBigCardGrid">
      {title && <h3 className={styles.title}>{title}</h3>}
      <Grid
        IterableComponent={BigCardWithSkeleton}
        componentPropsList={componentPropsList}
        numberOfRows={numberOfRows}
        numberOfColumns={Math.min(numberOfColumns, componentPropsList.length)}
        adRenderers={adRenderers}
      />
    </IntercomTracking>
  );
}
